"use client";

import type { DragEndEvent } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trans } from "@lingui/react/macro";
import { GripVerticalIcon } from "lucide-react";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type OrderItem = {
  id: string;
  label: React.ReactNode;
  detail?: React.ReactNode;
};

type Props = {
  title: React.ReactNode;
  badges?: React.ReactNode;
  items: OrderItem[];
  order: string[];
  loading?: boolean;
  fallbackLabel: string;
  onChange: (ids: string[]) => void;
  className?: string;
  enableDetailTooltip?: boolean;
  maxHeight?: string;
};

/**
 * 並び替え用の共通リストコンポーネント。
 * DnDによる順序変更とローディング／空表示を一元化する。
 */
export function OrderList({
  title,
  badges,
  items,
  order,
  loading = false,
  fallbackLabel,
  onChange,
  className,
  enableDetailTooltip = false,
  maxHeight = "24rem",
}: Props) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const itemMap = useMemo(
    () => new Map(items.map((item) => [item.id, item])),
    [items],
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIdx = order.indexOf(String(active.id));
    const newIdx = order.indexOf(String(over.id));
    if (oldIdx === -1 || newIdx === -1) return;
    const next = [...order];
    next.splice(oldIdx, 1);
    next.splice(newIdx, 0, String(active.id));
    onChange(next);
  };

  const ListItem = ({ id, index }: { id: string; index: number }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id });
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };
    const content = (
      <li
        ref={setNodeRef}
        style={style}
        className="grid grid-cols-[auto_auto_1fr] items-center gap-2 rounded-md border px-3 py-2 bg-card shadow-sm cursor-grab active:cursor-grabbing hover:bg-muted/50 transition-colors"
        {...attributes}
        {...listeners}
      >
        <GripVerticalIcon
          className="h-4 w-4 text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <span className="text-xs text-muted-foreground w-8 text-right shrink-0">
          {index + 1}
        </span>
        <span className="font-medium truncate min-w-0">
          {itemMap.get(id)?.label ?? <Trans>Unknown</Trans>}
        </span>
      </li>
    );

    if (!enableDetailTooltip || !itemMap.get(id)?.detail) return content;

    return (
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent
          side="right"
          className="max-w-xs space-y-1 text-xs bg-popover text-popover-foreground border border-border shadow-md"
        >
          {itemMap.get(id)?.detail}
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={80} skipDelayDuration={0}>
      <div className={`space-y-2 ${className ?? ""}`}>
        <div className="flex flex-col gap-1">
          <div className="font-medium text-sm md:text-base">{title}</div>
          {badges ? (
            <div className="text-[11px] tracking-wide text-muted-foreground flex items-center gap-2">
              {badges}
            </div>
          ) : null}
        </div>

        <div className="space-y-2" style={{ maxHeight, overflow: "auto" }}>
          <div className="text-muted-foreground text-xs font-medium">
            <Trans>New order</Trans>
          </div>

          {!mounted || loading ? (
            <div className="space-y-1">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : order.filter((id) => itemMap.has(id)).length ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={order}
                strategy={verticalListSortingStrategy}
              >
                <ol className="space-y-2 pl-0">
                  {order.map((id, idx) =>
                    itemMap.has(id) ? (
                      <ListItem key={id} id={id} index={idx} />
                    ) : null,
                  )}
                </ol>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="text-muted-foreground text-sm">{fallbackLabel}</div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}
